import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {GeometryAttributeNode, curveGeometryAttributeLikeMarker} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {createCurveGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, GeometryValueRefData} from "#template/nodes/geometry-graph/interfaces"
import type {ScalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {scalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {CurveGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext, ValueRef} from "#template/geometry-processing/geometry-graph"
import {Primitives} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type CurveGeometryLine1Parameters = {
    x0: ScalarArg | null
    x1: ScalarArg | null
    numSegments: ScalarArg | null
}

const definition: TemplateNodeDefinition<CurveGeometryLine1Parameters> = {
    nodeClass: "CurveGeometryLine1",
    validation: {
        parameters: z.object({
            x0: z.nullable(scalarArg),
            x1: z.nullable(scalarArg),
            numSegments: z.nullable(scalarArg),
        }),
        returns: getTemplateNodeReturns(),
    },
}
const resolveScalarArg = (value: GeometryValueData | number): GeometryValueRefData | number => (typeof value === "number" ? value : value.ref)

@registerTemplateGraphNode
export class CurveGeometryLine1 extends GeometryAttributeNode<1, CurveGeometryLine1Parameters> implements CurveGeometryAttributeLikeNode {
    get [curveGeometryAttributeLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: CurveGeometryLine1Parameters,
    ): parameters is CurveGeometryLine1Parameters & NonNullableParameters<CurveGeometryLine1Parameters> {
        return parameters.x0 !== null && parameters.x1 !== null && parameters.numSegments !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<CurveGeometryLine1Parameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<1> | null>; invalidator?: BuilderInlet<null>} {
        const {x0, x1, numSegments} = parameters

        const [x0Valid, x0Invalid] = scope.branch(evaluator.evaluate(x0))
        const [x1Valid, x1Invalid] = scope.branch(evaluator.evaluate(x1))
        const [numSegmentsValid, numSegmentsInvalid] = scope.branch(evaluator.evaluate(numSegments))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(x0Valid, x1Valid, numSegmentsValid, ctx),
            ([x0, x1, numSegments, ctx]) =>
                createCurveGeometryAttributeData(
                    applyGeometryOperator(
                        (x0: ValueRef | number, x1: ValueRef | number, numSegments: ValueRef | number) => Primitives.line1(ctx, x0, x1, numSegments),
                        resolveScalarArg(x0),
                        resolveScalarArg(x1),
                        resolveScalarArg(numSegments),
                    ),
                ),
            "curveGeometryLine1",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(x0Invalid, x1Invalid, numSegmentsInvalid)}
    }
}
